// Per-slide talking points for the private Presenter View.
// Keyed by `${view}-${index}` where view is 'main' or 'appendix'.
export const PRESENTER_NOTES = {
  "main-0": {
    title: "Cover — Your name is on the door",
    points: [
      "Open warm and personal — this is a private walkthrough built for them.",
      "Set the frame: not a product pitch, a look at how their firm actually runs.",
      "Ask permission to dive in, then advance.",
    ],
  },
  "main-1": {
    title: "AI Maturity — where they are today",
    points: [
      "Tap the stage that matches their firm right now.",
      "Emphasize: value comes from knowing your stage + the ONE next move — not sophistication.",
      "Call out the Stage 2→3 stall: pilots that never get an owner turn into disconnected tools.",
      "Land it: the fix is integration + ownership, not more tools.",
    ],
  },
  "main-2": {
    title: "Cost of inaction",
    points: [
      "Drag the sliders to their real numbers — make the leak personal.",
      "Missed intake + slow follow-up = signed cases walking to the firm that answered.",
      "Anchor the annual figure before moving on.",
    ],
  },
  "main-3": {
    title: "Why AI hasn't worked yet",
    points: [
      "Generic bots rebound because they don't know the firm.",
      "Flip a card: contrast 'it made more work' vs. the handcrafted fix.",
      "Bridge: the difference is context — which is the next slide.",
    ],
  },
  "main-4": {
    title: "Context is in the conversations",
    points: [
      "Every call carries the facts that move a case.",
      "Lexafide listens, extracts, and builds a living case context agents share.",
      "Walk the transcript → context web as it reveals.",
    ],
  },
  "main-5": {
    title: "Meet the agents",
    points: [
      "Toggle Inbound vs Outbound — inbound they dial in, outbound we call them.",
      "Pick the agent that maps to their biggest bottleneck.",
      "Offer the live demo: for outbound, take their number; for inbound, the dial-in.",
      "Each agent is handcrafted to their workflows, data model, and voice.",
    ],
  },
  "main-6": {
    title: "Hear it. See it.",
    points: [
      "Play the real recording of the call just placed.",
      "Point at the CMS panel filling live — name, matter type, injury.",
      "This is the same pipeline running in firms today.",
    ],
  },
  "main-7": {
    title: "The proof",
    points: [
      "Real metrics: fewer human-answered calls, higher routing accuracy, more qualified leads.",
      "Let the count-up stats land; read one testimonial aloud.",
    ],
  },
  "main-8": {
    title: "Built for your practice / Fit",
    points: [
      "Their workflows, their stack, their control.",
      "Show the confidence threshold — humans stay in control, they set the bar.",
    ],
  },
  "main-9": {
    title: "How it works — ~14 days",
    points: [
      "Discovery → build → test → live.",
      "Emphasize thorough testing before go-live; walk the timeline.",
    ],
  },
  "main-10": {
    title: "Close — let's map your operations",
    points: [
      "No slides, no pitch — bring what's burying the team.",
      "Direct ask: book 30 minutes. Make the next step concrete.",
    ],
  },
  "appendix-0": {
    title: "Appendix — Pricing",
    points: ["Use only if asked; frame around ROI vs. the cost-of-inaction number."],
  },
  "appendix-1": {
    title: "Appendix — Security & data",
    points: ["Reassure on data handling, access, and compliance posture."],
  },
  "appendix-2": {
    title: "Appendix — Built for your practice",
    points: ["Deeper fit detail for technical or ops-minded stakeholders."],
  },
};

export const getNotes = (view, index, aIndex) => {
  const key = view === "appendix" ? `appendix-${aIndex}` : `main-${index}`;
  return PRESENTER_NOTES[key] || { title: "", points: [] };
};

// --- Editable overrides (persisted in localStorage, per browser) ---
const STORE_KEY = "lexafide-presenter-notes";

export const noteKey = (view, index, aIndex) =>
  view === "appendix" ? `appendix-${aIndex}` : `main-${index}`;

const readOverrides = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "{}");
  } catch {
    return {};
  }
};

// Notes merged with any saved edits for a slide.
export const getMergedNotes = (view, index, aIndex) => {
  const key = noteKey(view, index, aIndex);
  const base = PRESENTER_NOTES[key] || { title: "", points: [] };
  const ov = readOverrides()[key];
  return ov ? { title: ov.title, points: ov.points } : base;
};

export const hasOverride = (view, index, aIndex) =>
  Boolean(readOverrides()[noteKey(view, index, aIndex)]);

// Save (or with title="" & points=[] effectively blank) an edited note.
export const saveNotes = (view, index, aIndex, title, points) => {
  const all = readOverrides();
  all[noteKey(view, index, aIndex)] = {
    title,
    points: points.map((p) => p.trim()).filter(Boolean),
  };
  localStorage.setItem(STORE_KEY, JSON.stringify(all));
};

// Remove an edit, reverting to the built-in default.
export const resetNotes = (view, index, aIndex) => {
  const all = readOverrides();
  delete all[noteKey(view, index, aIndex)];
  localStorage.setItem(STORE_KEY, JSON.stringify(all));
};
